/**
 * Image utilities for ClutterLogix
 * Handles image URL generation, caching, and optimization
 */

// Import the getBackendBaseUrl function
import { getBackendBaseUrl, isImageCacheBustingEnabled } from './configUtils';
/**
 * Get the best image URL for a box using ID-based endpoints for optimal performance
 * @param {Object} box - Box object with id, user_id, and custom_image_id
 * @returns {string|null} - Optimized image URL or null if no image
 */
export function getBestBoxImageUrl(box) {
  if (!box) {
    console.warn('getBestBoxImageUrl: No box provided');
    return null;
  }

  try {
    const baseUrl = getBackendBaseUrl(); // Get base URL
    
    // V2 API: Use new ID-based endpoints for optimal performance
    // Always use ID-based endpoint if box has custom_image_id and id
    // Default user_id to the box's user_id or fall back to current user context
    if ((box.custom_image_id || box.customImageId) && box.id) {
      const imageId = box.custom_image_id || box.customImageId;
      const userId = box.user_id || box.userId;
      
      if (!userId) {
        console.warn(`Box ${box.id} has image but no user_id - cannot generate image URL`);
        return null;
      }
      
      console.log(`Processing custom_image_id for box ${box.id}:`, imageId);
      
      // NEW: Use ID-based endpoint for better performance and security
      const idBasedUrl = `${baseUrl}/api/v2/files/box/${box.id}/${userId}`;
      
      // Add cache busting if enabled
      if (isImageCacheBustingEnabled()) {
        const cacheBuster = Date.now();
        const finalUrl = `${idBasedUrl}?t=${cacheBuster}`;
        console.log(`Using V2 ID-based API with cache busting for box ${box.id}:`, finalUrl);
        return finalUrl;
      }
      
      console.log(`Using V2 ID-based API for box ${box.id}:`, idBasedUrl);
      return idBasedUrl;
    }
    
    console.log(`No image found for box ${box.id}`);
    return null;
    
  } catch (error) {
    console.error('Error generating box image URL:', error);
    return null;
  }
}

// Default placeholder image as Base64 string for offline availability
// This is a simple gray placeholder image
export const placeholderImage = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAGQAAABkCAYAAABw4pVUAAAABmJLR0QA/wD/AP+gvaeTAAAAB3RJTUUH4QQQEwkDjI+mVAAAAStJREFUeNrt3MENgjAYhmFKXIRFGIVRGIVRGIVFPBiN0YTEkO+H90nuHulL+9EGzTkBAAAAAAAAAK+1not+j3IuZdyGMdxzGfdS7qWMYifFBvGcX5NyLY2oS5n5ORfNf5P9DOZc9FOaIoZnVYrQnSY00ZTvLRGbj5LoTBO7aO2ExNhva40Ynh95lWLLWt9YV2m3rPYbw0S1Yk2VJVLfIttSbFnrb1mtvxlMWCvWVlmiRmFjwZZVfcuyvmCJYcuqvm1Z4LHxP7KGRrQzITG2rbUTEmPbWj8hMdasaiek9TXL+oIlhi3L+gIAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAID/9wDq3mt7uObQRQAAAABJRU5ErkJggg==';

/**
 * Get optimized image URL based on size
 * @param {string} imageUrl - Original image URL
 * @param {string} size - Size variant ('small', 'medium', 'large', 'original')
 * @returns {string} Optimized image URL
 */
export const getOptimizedImageUrl = (imageUrl, size = 'medium') => {
  if (!imageUrl) {
    console.log('Image URL is empty, using placeholder');
    return placeholderImage;
  }
  
  // If the browser is offline, don't immediately return placeholder
  // This allows the OfflineImage component to check the cache first
  if (!navigator.onLine) {
    console.log('Browser is offline for:', imageUrl, '- allowing cache check');
    // Continue processing to return an appropriate URL that can be checked in the cache
  }

  try {
    // Handle static assets
    if (imageUrl.startsWith('/static/')) {
      console.log(`Static asset detected: ${imageUrl}`);
      return imageUrl;
    }

    // CRITICAL: NEVER return GCS URLs directly - they cause CORS errors!
    // All images MUST be served through the backend API for proper CORS handling.
    if (imageUrl.startsWith('https://storage.googleapis.com/')) {
      console.error(`GCS URL detected - this will cause CORS errors! Use getBestItemImageUrl or getBestBoxImageUrl with proper item/box objects instead:`, imageUrl);
      // Return placeholder since we cannot serve GCS URLs directly due to CORS
      return placeholderImage;
    }

    // Handle other absolute URLs
    if (imageUrl.startsWith('http')) {
      console.log(`Absolute URL detected: ${imageUrl}`);
      return imageUrl;
    }
    
    // For legacy backend file paths, we cannot convert to the deprecated endpoint
    // The caller should use getBestItemImageUrl or getBestBoxImageUrl with proper item/box objects
    if (imageUrl.includes('/uploads/') || imageUrl.includes('/api/v1/files/')) {
      console.warn(`Legacy file path detected. Use getBestItemImageUrl or getBestBoxImageUrl instead:`, imageUrl);
      // Return placeholder since we can't serve this without proper item/box context
      return placeholderImage;
    }
    
    // If it's already a V2 API URL, use it as-is
    if (imageUrl.includes('/api/v2/files/')) {
      console.log(`Already V2 API URL: ${imageUrl}`);
      return imageUrl;
    }
    
    // For any other format, return as-is and let the browser handle it
    console.log(`Using image URL as-is: ${imageUrl}`);
    return imageUrl;
  } catch (e) {
    console.warn('Error processing image URL:', e);
    return placeholderImage;
  }
};

export const checkImageExists = (url) => {
  return new Promise((resolve) => {
    if (!url) {
      console.log('URL is empty, image does not exist');
      resolve(false);
      return;
    }
    
    const img = new Image();
    img.onload = () => {
      console.log(`Image loaded successfully: ${url}`);
      resolve(true);
    };
    img.onerror = () => {
      console.log(`Failed to load image: ${url}`);
      resolve(false);
    };
    img.src = url;
  });
};

/**
 * Get appropriate image URL with fallback to placeholder
 * @param {string} imageUrl - Original image URL
 * @param {string} size - Size variant
 * @returns {string} Image URL or placeholder
 */
export const getImageWithFallback = async (imageUrl, size = 'medium') => {
  if (!imageUrl) {
    return placeholderImage;
  }
  
  const optimizedUrl = getOptimizedImageUrl(imageUrl, size);
  const exists = await checkImageExists(optimizedUrl);
  
  return exists ? optimizedUrl : placeholderImage;
};

/**
 * Extract path portion from a URL
 * Useful for avoiding cross-origin issues with IP addresses
 * @param {string} url - The full URL
 * @returns {string} The path portion or original URL
 */
export const getPathFromUrl = (url) => {
  if (!url) return '';
  
  // If it's already a relative path, return it
  if (url.startsWith('/')) return url;
  
  try {
    const urlObj = new URL(url);
    return urlObj.pathname; // Just get the path part
  } catch (e) {
    console.warn('Error extracting path from URL:', e);
    return url;
  }
};

/**
 * Get the best available image URL from an item object
 * @param {Object} item - Item object from API
 * @param {Object|string} options - Options for image serving or legacy size parameter
 * @param {boolean} options.thumbnail - Whether to request a thumbnail (faster loading)
 * @param {number} options.size - Thumbnail size in pixels (50-800, default: 300)
 * @returns {string} Best available image URL or placeholder
 */
export const getBestItemImageUrl = (item, options = {}) => {
  // Handle legacy string parameter for backward compatibility
  if (typeof options === 'string') {
    options = { size: options };
  }
  
  const { thumbnail = false, size = 300 } = options;
  if (!item) {
    console.log('Item is null or undefined, using placeholder');
    return placeholderImage;
  }
  
  // If the browser is offline, still return an URL to try loading
  // (The request might be intercepted by service worker in future PWA implementation)
  if (!navigator.onLine) {
    console.log('Device is offline for item:', item.id, '- returning URL anyway');
    
    // If the item has explicit cached image data, use that
    if (item.cachedImageData) {
      console.log('Using embedded cached image data for item:', item.id);
      return item.cachedImageData;
    }
  }
  
  try {
    const baseUrl = getBackendBaseUrl(); // Get base URL
    
    // V2 API: Use new ID-based endpoints for optimal performance
    // Get user_id from item or fallback to current user from JWT token
    let userId = item.user_id;
    console.log(`getBestItemImageUrl: item ${item.id} - user_id from item: ${userId}, image_url: ${item.image_url?.substring(0, 50)}`);
    
    if (!userId) {
      // Try to decode user_id from JWT token in localStorage
      try {
        const token = localStorage.getItem('cubxly_token');
        if (token) {
          // JWT tokens are base64 encoded: header.payload.signature
          const parts = token.split('.');
          if (parts.length === 3) {
            // Decode the payload (second part)
            const payload = JSON.parse(atob(parts[1]));
            userId = payload.user_id || payload.sub;
            if (userId) {
              // Convert string to number if needed
              userId = parseInt(userId, 10);
              console.log(`getBestItemImageUrl: item ${item.id} - extracted user_id from JWT: ${userId}`);
            } else {
              console.warn(`getBestItemImageUrl: item ${item.id} - JWT payload has no user_id or sub:`, payload);
            }
          } else {
            console.warn(`getBestItemImageUrl: item ${item.id} - Invalid JWT format, parts: ${parts.length}`);
          }
        } else {
          console.warn(`getBestItemImageUrl: item ${item.id} - No token in localStorage`);
        }
      } catch (e) {
        console.warn(`getBestItemImageUrl: item ${item.id} - Failed to extract user_id from JWT:`, e);
      }
    }
    
    // Final fallback: check sessionStorage for cached user_id
    if (!userId) {
      const cachedUserId = sessionStorage.getItem('cubxly_current_user_id');
      if (cachedUserId) {
        userId = parseInt(cachedUserId, 10);
        console.log(`getBestItemImageUrl: item ${item.id} - using cached user_id from sessionStorage: ${userId}`);
      }
    }
    
    // Cache the user_id for future use if we found one
    if (userId && !sessionStorage.getItem('cubxly_current_user_id')) {
      sessionStorage.setItem('cubxly_current_user_id', userId.toString());
    }
    
    if (item.image_url && item.id && userId) {
      console.log(`Processing image_url for item ${item.id}:`, item.image_url);
      
      // CRITICAL: NEVER return GCS URLs directly - they cause CORS errors!
      // All images MUST be served through the backend API for proper CORS handling.
      // Even if image_url contains a full GCS URL, we route through the API endpoint.
      // The backend will fetch from GCS and serve with proper CORS headers.
      
      // Use ID-based endpoint for ALL images (including GCS-stored ones)
      let idBasedUrl = `${baseUrl}/api/v2/files/item/${item.id}/${userId}`;
      
      // Build query parameters
      const params = new URLSearchParams();
      
      // Add thumbnail parameters if requested
      if (thumbnail) {
        params.set('thumbnail', 'true');
        params.set('size', size.toString());
      }
      
      // Add cache busting if enabled globally
      if (isImageCacheBustingEnabled()) {
        const cacheBuster = Date.now();
        params.set('t', cacheBuster.toString());
      }
      
      // Smart cache busting: Add a hash of the image_url to the URL PATH (not query param)
      // This ensures browser treats it as a different URL when backend image_url changes
      // (e.g., from group photo to cropped image, or from local:// to GCS URL after
      // background upload completes — hashing only the basename made those two cases
      // collide, so the browser kept serving the broken local:// response).
      // Using path segment instead of query param because browsers cache by path.
      //
      // The REACT_APP_IMAGE_CACHE_VERSION prefix is a global cache breaker we can
      // bump (in vercel.json) when something poisons every browser's cache and we
      // need to evict the bad responses across all users at once. Bump it once,
      // every URL changes, every cache misses, every browser refetches and gets
      // the now-correct response.
      if (item.image_url) {
        // Hash the FULL image_url (including scheme) so local://X.jpg and
        // https://storage.googleapis.com/.../X.jpg get distinct cache keys.
        const hashInput = item.image_url;
        let hash = 0;
        for (let i = 0; i < hashInput.length; i++) {
          const char = hashInput.charCodeAt(i);
          hash = ((hash << 5) - hash) + char;
          hash = hash & hash; // Convert to 32bit integer
        }
        const cacheVersion = process.env.REACT_APP_IMAGE_CACHE_VERSION || '1';
        idBasedUrl += `/v${cacheVersion}-${Math.abs(hash).toString(36)}`;
      }
      
      // Append query parameters if any exist (for thumbnail, etc.)
      if (params.toString()) {
        idBasedUrl += `?${params.toString()}`;
      }
      
      const logMessage = thumbnail ? 
        `Using V2 ID-based API with ${size}px thumbnail for item ${item.id}:` :
        `Using V2 ID-based API for item ${item.id}:`;
      console.log(logMessage, idBasedUrl);
      return idBasedUrl;
    }
    
    // No valid ID-based URL could be generated - item is missing required data
    if (item.image_url) {
      console.warn(`Item ${item.id} has image_url but missing user_id - cannot generate secure image URL`);
    }
    
    // Fallback: Try /uploads/ direct serving for legacy compatibility
    const imageId = item.storage_file_id || item.custom_image_id || item.customImageId;
    
    if (imageId) {
      // Try direct uploads folder access first (legacy compatibility)
      const uploadsUrl = `${baseUrl}/uploads/${imageId}`;
      console.log(`Trying uploads folder for item ${item.id}:`, uploadsUrl);
      return uploadsUrl;
    }
    
    // No valid image ID or URL available, return placeholder
    console.log(`No image data found for item ${item.id}, using placeholder`);
    return placeholderImage;
  } catch (error) {
    console.error('Error processing image for item:', item.id, error);
    return placeholderImage;
  }
};

// Cache busting functionality
// let imageCacheBustingEnabled = false;

export const setImageCacheBustingEnabled = (enabled) => {
  // imageCacheBustingEnabled = enabled;
  console.log('Image cache busting', enabled ? 'enabled' : 'disabled');
};

/**
 * Get a thumbnail URL for an item (optimized for fast loading)
 * @param {Object} item - Item object with image_url and id
 * @param {number} size - Thumbnail size in pixels (default: 300)
 * @returns {string} - Thumbnail image URL
 */
export const getItemThumbnailUrl = (item, size = 300) => {
  return getBestItemImageUrl(item, { thumbnail: true, size });
};

// Functions are already exported above as function declarations
